import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { showMessage } from 'react-native-flash-message';
import { Colors, Typography } from '../../theme';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import ReviewCard, { type ReviewMenuOption, ReviewOptionsSheet } from './ReviewCard';
import ReportReviewSheet from './ReportReviewSheet';
import WriteReviewSheet from './WriteReviewSheet';
import TonyteModal from '../../components/TonyteModal';
import {
  clearReviewDraft,
  deletePartnerReview,
  moderatePartnerReview,
  type PartnerReview,
  type PartnerReviewStats,
  reviewErrorKey,
  type ReviewModerationAction,
  type StarValue,
} from './reviewsApi';
import { formatAverage, reviewPalette, STAR_GOLD, StarRow } from './StarRating';
import { useMyPartnerReview, usePartnerReviewList } from './usePartnerReviews';

export type ReviewsTabHandle = { loadMore: () => void };

const STARS: StarValue[] = [5, 4, 3, 2, 1];

// The partner profile's Reviews tab. Reviews are of the partner as a whole,
// not of single events.
const ReviewsTab = forwardRef<ReviewsTabHandle, {
  venueId: string;
  venueName: string;
  userId: string | null;
  isOwner: boolean;
  isAdmin: boolean;
  stats: PartnerReviewStats;
  statsLoaded: boolean;
}>(({ venueId, venueName, userId, isOwner, isAdmin, stats, statsLoaded }, ref) => {
  const { t, tp, language } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  const selectionColor = theme.isDark ? STAR_GOLD : '#A94D08';
  const [star, setStar] = useState<StarValue | null>(null);
  const [writeOpen, setWriteOpen] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);
  const [menuReview, setMenuReview] = useState<PartnerReview | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { review: myReview } = useMyPartnerReview(venueId, userId);
  const list = usePartnerReviewList(venueId, star);
  const { loadMore, reload } = list;

  useImperativeHandle(ref, () => ({ loadMore }), [loadMore]);

  // The viewer's own review is pinned on top (in any status), so it's left
  // out of the list below.
  const others = useMemo(() => list.reviews.filter(item => item.reviewerUid !== userId), [list.reviews, userId]);
  const showOwnPinned = Boolean(myReview) && (!star || myReview!.rating === star);

  const requireSignIn = useCallback(() => {
    if (userId) return true;
    Alert.alert(t('auth.signInRequired'), t('partnerReviews.signInToReview'));
    return false;
  }, [t, userId]);

  const openWrite = () => {
    if (!requireSignIn()) return;
    if (myReview?.status === 'removed') {
      Alert.alert(t('partnerReviews.titleEdit'), t('partnerReviews.errors.removed'));
      return;
    }
    setWriteOpen(true);
  };

  const confirmDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const deleteReview = async () => {
    setDeleteConfirmOpen(false);
    try {
      await deletePartnerReview(venueId);
      await clearReviewDraft(venueId);
      showMessage({ message: t('partnerReviews.deleted'), type: 'success' });
      reload();
    } catch (error) {
      console.warn('[Tonyte][PartnerReviews] delete failed', { venueId, error });
      Alert.alert(t('common.error'), t(reviewErrorKey(error)));
    }
  };

  const moderate = (review: PartnerReview, action: ReviewModerationAction) => {
    const run = async () => {
      try {
        await moderatePartnerReview(review.id, action);
        showMessage({ message: t(`partnerReviews.moderated.${action}`), type: 'success' });
        reload();
      } catch (error) {
        console.warn('[Tonyte][PartnerReviews] moderation failed', { reviewId: review.id, action, error });
        Alert.alert(t('common.error'), t(reviewErrorKey(error)));
      }
    };
    if (action === 'restore') {
      run();
      return;
    }
    Alert.alert(t(`partnerReviews.confirm.${action}Title`), t(`partnerReviews.confirm.${action}Body`), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t(`partnerReviews.actions.${action}`), style: 'destructive', onPress: run },
    ]);
  };

  const menuOptions = (review: PartnerReview): ReviewMenuOption[] => {
    const own = review.reviewerUid === userId;
    const options: ReviewMenuOption[] = [];
    if (own) {
      if (review.status !== 'removed') options.push({ key: 'edit', label: t('partnerReviews.actions.edit'), icon: 'edit-2', onPress: openWrite });
      options.push({ key: 'delete', label: t('partnerReviews.actions.delete'), icon: 'trash-2', destructive: true, onPress: confirmDelete });
      return options;
    }
    options.push({ key: 'report', label: t('partnerReviews.actions.report'), icon: 'flag', onPress: () => { if (requireSignIn()) setReportId(review.id); } });
    if (isAdmin) {
      if (review.status === 'hidden') options.push({ key: 'restore', label: t('partnerReviews.actions.restore'), icon: 'eye', onPress: () => moderate(review, 'restore') });
      else if (review.status === 'published') options.push({ key: 'hide', label: t('partnerReviews.actions.hide'), icon: 'eye-off', destructive: true, onPress: () => moderate(review, 'hide') });
      if (review.status !== 'removed') options.push({ key: 'remove', label: t('partnerReviews.actions.remove'), icon: 'x-octagon', destructive: true, onPress: () => moderate(review, 'remove') });
    }
    return options;
  };

  const maxBucket = Math.max(1, ...STARS.map(item => stats.distribution[item]));
  const hasReviews = stats.count > 0;

  return (
    <View style={styles.wrap} testID="reviews-tab">
      <View style={[styles.overview, { backgroundColor: palette.surface, borderColor: palette.border }]}>
        <View style={styles.averageBlock}>
          <Text style={[styles.averageNumber, { color: palette.text }]} testID="reviews-average">
            {hasReviews ? formatAverage(stats.average, language) : '–'}
          </Text>
          <StarRow value={hasReviews ? Math.round(stats.average * 10) / 10 : 0} size={15} />
          <Text style={[styles.averageMeta, { color: palette.muted }]} testID="reviews-count">
            {statsLoaded ? tp('partnerReviews.ratingCount', stats.count) : ' '}
          </Text>
        </View>
        <View style={styles.distribution}>
          {STARS.map(item => {
            const count = stats.distribution[item];
            const active = star === item;
            return (
              <TouchableOpacity
                key={item}
                style={styles.barRow}
                onPress={() => setStar(active ? null : item)}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`${tp('partnerReviews.starCount', item)}: ${count}`}
                testID={`distribution-${item}`}
              >
                <Text style={[styles.barLabel, { color: active ? selectionColor : palette.muted }]}>{item}</Text>
                <View style={[styles.barTrack, { backgroundColor: palette.track }]}>
                  <View style={[styles.barFill, { width: `${(count / maxBucket) * 100}%` }, !count && styles.barEmpty]} />
                </View>
                <Text style={[styles.barCount, { color: palette.faint }]}>{count}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {isOwner ? (
        <Text style={[styles.ownerNote, { color: palette.muted }]}>{t('partnerReviews.ownProfile')}</Text>
      ) : (
        <TouchableOpacity style={styles.writeButton} onPress={openWrite} accessibilityRole="button" testID="write-review">
          <Icon name={myReview ? 'edit-2' : 'star'} size={16} color="#fff" />
          <Text style={styles.writeText}>{myReview ? t('partnerReviews.edit') : t('partnerReviews.write')}</Text>
        </TouchableOpacity>
      )}

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filters}>
        {([null, ...STARS] as (StarValue | null)[]).map(item => {
          const active = star === item;
          return (
            <TouchableOpacity
              key={item ?? 'all'}
              onPress={() => setStar(item)}
              style={[styles.chip, { borderColor: active ? selectionColor : palette.border, backgroundColor: active ? 'rgba(255,185,56,0.16)' : palette.surface }]}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={item ? tp('partnerReviews.starCount', item) : t('partnerReviews.filterAll')}
              testID={`filter-${item ?? 'all'}`}
            >
              {item ? <Text style={[styles.chipText, { color: active ? selectionColor : palette.text }]}>{`${item} ★`}</Text>
                : <Text style={[styles.chipText, { color: active ? selectionColor : palette.text }]}>{t('partnerReviews.filterAll')}</Text>}
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <View style={styles.list}>
        {showOwnPinned ? <ReviewCard review={myReview!} isOwn onMenu={() => setMenuReview(myReview)} /> : null}
        {list.loading ? (
          <ActivityIndicator color={Colors.purple} style={styles.spinner} testID="reviews-loading" />
        ) : list.error ? (
          <View style={styles.empty}>
            <Text style={[styles.emptyText, { color: palette.muted }]}>{t('partnerReviews.loadFailed')}</Text>
            <TouchableOpacity onPress={reload} accessibilityRole="button"><Text style={[styles.link, { color: palette.accent }]}>{t('partnerReviews.retry')}</Text></TouchableOpacity>
          </View>
        ) : !others.length && !showOwnPinned ? (
          <View style={styles.empty} testID="reviews-empty">
            <Icon name="star" size={24} color={STAR_GOLD} />
            <Text style={[styles.emptyTitle, { color: palette.text }]}>
              {star ? t('partnerReviews.emptyFiltered').replace('{count}', String(star)) : t('partnerReviews.empty')}
            </Text>
            {!star && !isOwner ? <Text style={[styles.emptyText, { color: palette.muted }]}>{t('partnerReviews.emptySub')}</Text> : null}
          </View>
        ) : (
          others.map(item => <ReviewCard key={item.id} review={item} isOwn={false} onMenu={() => setMenuReview(item)} />)
        )}
        {list.loadingMore ? <ActivityIndicator color={Colors.purple} style={styles.spinner} /> : null}
        {!list.loading && list.hasMore && !list.loadingMore ? (
          <TouchableOpacity style={[styles.moreButton, { borderColor: palette.border }]} onPress={loadMore} accessibilityRole="button" testID="reviews-more">
            <Text style={[styles.moreText, { color: palette.text }]}>{t('partnerReviews.showMore')}</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      <ReviewOptionsSheet
        visible={Boolean(menuReview)}
        title={t('partnerReviews.menu')}
        options={menuReview ? menuOptions(menuReview) : []}
        onClose={() => setMenuReview(null)}
      />
      <WriteReviewSheet
        visible={writeOpen}
        venueId={venueId}
        venueName={venueName}
        existing={myReview}
        onClose={() => setWriteOpen(false)}
        onSaved={created => {
          setWriteOpen(false);
          showMessage({ message: t(created ? 'partnerReviews.posted' : 'partnerReviews.updated'), type: 'success' });
          reload();
        }}
      />
      <TonyteModal
        visible={deleteConfirmOpen}
        title={t('partnerReviews.deleteTitle')}
        message={t('partnerReviews.deleteBody').replace('{name}', venueName)}
        icon="trash-2"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: () => setDeleteConfirmOpen(false) },
          { label: t('partnerReviews.actions.delete'), variant: 'danger', onPress: deleteReview, testID: 'review-delete-confirm' },
        ]}
        onDismiss={() => setDeleteConfirmOpen(false)}
      />
      <ReportReviewSheet
        reviewId={reportId}
        onClose={() => setReportId(null)}
        onReported={() => {
          setReportId(null);
          showMessage({ message: t('partnerReviews.reported'), type: 'success' });
        }}
      />
    </View>
  );
});

export default ReviewsTab;

const styles = StyleSheet.create({
  wrap: { paddingTop: 16, gap: 14 },
  overview: { flexDirection: 'row', gap: 16, borderRadius: 20, borderWidth: 1, padding: 16, alignItems: 'center' },
  averageBlock: { alignItems: 'center', gap: 5, minWidth: 92 },
  averageNumber: { fontSize: 38, lineHeight: 42, fontWeight: Typography.extrabold },
  averageMeta: { fontSize: 12, fontWeight: Typography.semibold, textAlign: 'center' },
  distribution: { flex: 1, gap: 5 },
  barRow: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 20 },
  barLabel: { width: 10, fontSize: 12, fontWeight: Typography.bold, textAlign: 'center' },
  barTrack: { flex: 1, height: 7, borderRadius: 4, overflow: 'hidden' },
  barFill: { height: 7, borderRadius: 4, backgroundColor: STAR_GOLD },
  barEmpty: { width: 0 },
  barCount: { minWidth: 22, fontSize: 11, textAlign: 'right' },
  ownerNote: { fontSize: 13, lineHeight: 18, textAlign: 'center' },
  writeButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 48, borderRadius: 16, backgroundColor: Colors.sunset },
  writeText: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  filters: { gap: 8, paddingRight: 4 },
  chip: { minHeight: 34, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  chipText: { fontSize: 13, fontWeight: Typography.bold },
  list: { gap: 10 },
  spinner: { marginVertical: 24 },
  empty: { alignItems: 'center', paddingVertical: 34, gap: 8 },
  emptyTitle: { fontSize: 15, fontWeight: Typography.extrabold, textAlign: 'center' },
  emptyText: { fontSize: 13, lineHeight: 18, textAlign: 'center' },
  link: { fontSize: 14, fontWeight: Typography.bold },
  moreButton: { minHeight: 44, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  moreText: { fontSize: 14, fontWeight: Typography.bold },
});
